import type { GetStaticPropsContext } from 'next'

import { Layout } from '~/components'
import type { Story } from '~/lib/storyblok'
import { contentTypes } from '~/storyblok/contentTypes'
import { useStoryblok } from '~/lib/storyblok/useStoryblok'
import { storyblok } from '~/storyblok/storyblok'
import { getPaths } from '~/storyblok/getPaths'
import Head from 'next/head'
import { Settings } from '~/contentTypes/Settings/types'
import { Page } from '~/contentTypes/Page'
import { Location } from '~/contentTypes/Location'
import { Event } from '~/contentTypes/Event'
import { Chef } from '~/contentTypes/Chef'
import { fetchSettings } from '~/contentTypes/Settings/fetchSettings'
import 'yet-another-react-lightbox/styles.css'

type Props = {
  story: Story // TODO: Story or ContentType?
  preview?: boolean
  settings: Settings
}

export default function Template({ story: initialStory, preview, settings }: Props) {
  const story = useStoryblok(initialStory, { preview, contentTypes })

  const title = `${initialStory.name} | Genussfestival Vaduz – ${settings.title}`

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={settings.description} />
        <meta property="og:title" content={title} key="og:title" />
        <meta property="og:description" content={settings.description} key="og:description" />
      </Head>
      <Layout title={settings.title} menu={settings.menu}>
        {story.component === 'Page' && <Page story={story} />}
        {story.component === 'Location' && <Location story={story} />}
        {story.component === 'Event' && <Event story={story} />}
        {story.component === 'Chef' && <Chef story={story} />}
      </Layout>
    </>
  )
}

export async function getStaticProps({ params, preview = false }: GetStaticPropsContext) {
  const slug = params?.slug && Array.isArray(params.slug) ? params.slug.join('/') : 'index'
  const story = await storyblok.getStory(slug, { preview })
  const settings = await fetchSettings({ preview })

  if (!story) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      story,
      preview,
      settings,
    },
    revalidate: 3600,
  }
}

export async function getStaticPaths() {
  const paths = await getPaths(storyblok)

  return {
    paths,
    fallback: false,
  }
}
